import clsx from "clsx";
import PropTypes from "prop-types";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Media } from "@/components/utils/Media";
import { usePadding } from "@/components/utils/Padding";
import "swiper/css";
import "./styles.scss";

const GalleryLayout = ({ data }) => {
  const galleryLayout = data?.gallery_layout || {};
  const customPadding = galleryLayout?.padding_component;
  const paddingStyle = usePadding(customPadding);

  const mode = galleryLayout?.layout_mode || "single_media";
  const grid = galleryLayout?.gallery_grid || {};
  const fullTop = !!grid?.full_img_position;
  const smallOnRight = !!grid?.grid_orientation;

  const ratio = grid?.column_split ? "50-50" : "68-32";
  const ratioCls = `ratio-${ratio}`;

  // Two images direction: 'row' | 'column'
  const twoDir = (grid?.two_column_direction || "row").toLowerCase();
  const mobileMode = grid?.gallery_mobile_mode || "grid";

  // single media
  if (mode === "single_media") {
    const media = galleryLayout?.media_component || {};

    return (
      <section className={clsx("gallery-section isolate")} style={paddingStyle}>
        <div className="container-revamp gallery-container gallery--1">
          <div className="single-media-wrapper relative">
            {/* Media */}
            <Media {...media} />
          </div>
        </div>
      </section>
    );
  }

  // grid mode
  const items = Array.isArray(grid?.gallery_grid_images) ? grid.gallery_grid_images : [];
  const images = items.map((it) => it?.image).filter((img) => img && img.url);
  const positions = items.map((it) => it?.img_position || "");
  const count = images.length;

  const layoutCls = `gallery--${Math.min(Math.max(count, 1), 4)}`;

  const Feature = ({ img, pos, tall = true }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, threshold: 0.2 });
    return (
      <motion.div
        ref={ref}
        className={clsx("full-image", pos && `pos-${pos}`, tall ? "h-[700px]" : "h-[430px]")}
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ type: "spring", stiffness: 100, damping: 20, delay: 0.15 }}
      >
        <img src={img.url} alt={img.alt || ""} className="w-full h-full object-cover rounded-3xl" />
      </motion.div>
    );
  };

  const Thumb = ({ img, pos }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, threshold: 0.2 });
    return (
      <motion.div
        ref={ref}
        className={clsx("grid-image", pos && `pos-${pos}`)}
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
      >
        <img src={img.url} alt={img.alt || ""} className="w-full h-full object-cover rounded-3xl" />
      </motion.div>
    );
  };

  const DesktopGrid = () => {
    if (count <= 0) return null;

    if (count === 2) {
      const directionClass =
        twoDir === "column"
          ? smallOnRight
            ? "flex-col column"
            : "flex-col-reverse"
          : smallOnRight
            ? "lg:flex-row"
            : "lg:flex-row-reverse";

      return (
        <div className={clsx("gallery-grid-container", layoutCls)}>
          <div className={clsx("gallery-grid", ratioCls, directionClass)}>
            <Thumb img={images[0]} pos={positions[0]} />
            <Thumb img={images[1]} pos={positions[1]} />
          </div>
        </div>
      );
    }

    if (count === 1) {
      return (
        <div
          className={clsx(
            "gallery-grid-container",
            fullTop ? "flex-col" : "flex-col-reverse",
            layoutCls
          )}
        >
          <Feature img={images[0]} pos={positions[0]} tall />
        </div>
      );
    }

    if (count <= 3) {
      const restImgs = images.slice(1);
      const restPos = positions.slice(1);
      return (
        <div
          className={clsx(
            "gallery-grid-container",
            fullTop ? "flex-col" : "flex-col-reverse",
            layoutCls
          )}
        >
          <Feature img={images[0]} pos={positions[0]} tall={fullTop} />
          {restImgs.length > 0 && (
            <div
              className={clsx(
                "gallery-grid flex-col",
                smallOnRight ? "lg:flex-row left-layout" : "lg:flex-row-reverse right-layout",
                ratioCls
              )}
            >
              {restImgs.map((img, i) => (
                <Thumb key={`thumb-${i}`} img={img} pos={restPos[i]} delay={0.3 + i * 0.15} />
              ))}
            </div>
          )}
        </div>
      );
    }

    const tilesImgs = images.slice(0, 4);
    const tilesPos = positions.slice(0, 4);
    return (
      <div className={clsx("gallery-grid-container", layoutCls)}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {tilesImgs.map((img, i) => (
            <div key={`tile-${i}`} className="h-[430px]">
              <Thumb img={img} pos={tilesPos[i]} delay={0.2 + i * 0.1} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const MobileSlider = () => (
    <Swiper className="lg:!hidden" spaceBetween={16} slidesPerView={1.1}>
      {images.map((img, i) => (
        <SwiperSlide key={`slide-${i}`}>
          <div className={clsx("w-full h-[430px]", positions[i] && `pos-${positions[i]}`)}>
            <img
              src={img.url}
              alt={img.alt || ""}
              className="w-full h-full object-cover rounded-3xl"
            />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );

  return (
    <section className={clsx("gallery-section")} style={paddingStyle}>
      <div className={clsx("container-revamp gallery-container", layoutCls)}>
        {mobileMode !== "slider" ? (
          <DesktopGrid />
        ) : (
          <>
            <div className="hidden lg:block">
              <DesktopGrid />
            </div>
            <MobileSlider />
          </>
        )}
      </div>
    </section>
  );
};

GalleryLayout.propTypes = {
  data: PropTypes.shape({
    gallery_layout: PropTypes.shape({
      layout_mode: PropTypes.oneOf(["single_media", "img_grid"]),
      media: PropTypes.object,
      gallery_grid: PropTypes.shape({
        full_img_position: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
        grid_orientation: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
        column_split: PropTypes.oneOf(["68-32", "50-50"]),
        gallery_grid_images: PropTypes.arrayOf(
          PropTypes.shape({
            image: PropTypes.shape({
              url: PropTypes.string,
              alt: PropTypes.string,
              title: PropTypes.string,
              ID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            }),
            img_position: PropTypes.string,
          })
        ),
        gallery_mobile_mode: PropTypes.oneOf(["grid", "slider"]),
      }),
    }),
  }),
};

export default GalleryLayout;
